import { useRef, useState } from "react";
import { ChevronsLeftRight, ImagePlus } from "lucide-react";
import { SectionTitle } from "./Brand";
import { Reveal } from "./Reveal";

const PlaceholderHalf = ({ label, tone, side }) => (
    <div
        className={`flex h-full w-full ${tone === "before" ? "bg-[#0A0D13]" : "bg-panel"}`}
    >
        <div
            className={`flex w-1/2 flex-col items-center justify-center gap-3 ${
                side === "left" ? "order-first" : "order-last ml-auto"
            }`}
        >
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-dashed border-neon/30 text-neon/60">
                <ImagePlus size={26} strokeWidth={1.5} />
            </div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-500">
                {label}
            </p>
            <p className="px-4 text-center text-xs text-slate-600">Photo réelle à venir</p>
        </div>
    </div>
);

const CompareSlot = ({ id, caption }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState(50);
    const dragging = useRef(false);

    const update = (clientX) => {
        const rect = ref.current.getBoundingClientRect();
        const pct = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.min(96, Math.max(4, pct)));
    };

    return (
        <figure data-testid={`compare-slot-${id}`}>
            <div
                ref={ref}
                role="slider"
                aria-label={`Comparateur avant après ${caption}`}
                aria-valuenow={Math.round(pos)}
                aria-valuemin={0}
                aria-valuemax={100}
                tabIndex={0}
                data-testid={`compare-slider-${id}`}
                className="card-line relative aspect-[4/3] cursor-ew-resize select-none overflow-hidden rounded-3xl"
                style={{ touchAction: "pan-y" }}
                onPointerDown={(e) => {
                    dragging.current = true;
                    ref.current.setPointerCapture(e.pointerId);
                    update(e.clientX);
                }}
                onPointerMove={(e) => dragging.current && update(e.clientX)}
                onPointerUp={() => (dragging.current = false)}
                onPointerCancel={() => (dragging.current = false)}
                onKeyDown={(e) => {
                    if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 4));
                    if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 4));
                }}
            >
                <div className="absolute inset-0">
                    <PlaceholderHalf label="Après" tone="after" side="right" />
                </div>
                <div
                    className="absolute inset-0"
                    style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
                >
                    <PlaceholderHalf label="Avant" tone="before" side="left" />
                </div>

                <div
                    className="absolute inset-y-0 w-px bg-neon/80 shadow-[0_0_16px_rgba(0,240,255,0.6)]"
                    style={{ left: `${pos}%` }}
                >
                    <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-neon/60 bg-noir/90 text-neon shadow-[0_0_20px_rgba(0,240,255,0.4)]">
                        <ChevronsLeftRight size={18} />
                    </span>
                </div>
            </div>
            <figcaption className="mt-3 text-center text-sm text-slate-500">{caption}</figcaption>
        </figure>
    );
};

const BeforeAfter = () => (
    <section id="avant-apres" data-testid="before-after-section" className="py-24 sm:py-32">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <Reveal>
                <SectionTitle
                    eyebrow="Avant / Après"
                    title="La preuve par l'image"
                    sub="Faites glisser le curseur pour comparer. Nos vraies réalisations photo arrivent très bientôt — aucun visuel générique, que du Clean My Life."
                />
            </Reveal>
            <div className="grid gap-8 md:grid-cols-2">
                <Reveal delay={0.05}>
                    <CompareSlot id="1" caption="Habitacle — nettoyage intérieur voiture" />
                </Reveal>
                <Reveal delay={0.15}>
                    <CompareSlot id="2" caption="Carrosserie — lavage automobile à domicile" />
                </Reveal>
            </div>
        </div>
    </section>
);

export default BeforeAfter;
